import { Node } from '@tiptap/pm/model';
import { getHTMLFromFragment } from '@tiptap/core';
import { tuiIsNodeIn, tuiParseColor } from '@taiga-ui/cdk';
function tuiDeleteNode(state, dispatch, nodeName) {
  const position = state.selection.$anchor;
  for (let depth = position.depth; depth > 0; depth--) {
    const node = position.node(depth);
    if (node.type.name === nodeName) {
      if (dispatch) {
        dispatch(state.tr.delete(position.before(depth), position.after(depth)).scrollIntoView());
      }
      return true;
    }
  }
  return false;
}
function tuiGetCurrentWordBounds(editor) {
  const {
    state
  } = editor;
  const {
    selection
  } = state;
  const {
    $anchor,
    empty
  } = selection;
  if (!empty) {
    return {
      from: selection.from,
      to: selection.to
    };
  }
  if ($anchor) {
    const {
      pos
    } = $anchor;
    const start = $anchor.start();
    const parent = $anchor.parent;
    const textBefore = parent.textBetween(0, pos - start, undefined, '\uFFFC');
    const textAfter = parent.textBetween(pos - start, parent.content.size, undefined, '\uFFFC');
    const wordBefore = textBefore
    // eslint-disable-next-line unicorn/prefer-string-replace-all
    .replaceAll(/\uFFFC/g, '').split(/\b/).pop();
    const wordAfter = textAfter
    // eslint-disable-next-line unicorn/prefer-string-replace-all
    .replaceAll(/\uFFFC/g, '').split(/\b/).shift();
    const from = pos - (wordBefore?.length ?? 0);
    const to = pos + (wordAfter?.length ?? 0);
    return {
      from,
      to
    };
  }
  return {
    from: selection.to,
    to: selection.to + 1
  };
}
function hasAttributes(markAttributes, attributes) {
  const keys = Object.keys(attributes);
  return !keys.length || !!keys.filter(key => attributes[key] === markAttributes[key]).length;
}
function findMarkInSet(marks, markType, attributes = {}) {
  return marks.find(({
    attrs,
    type
  }) => type === markType && hasAttributes(attrs, attributes)) || null;
}
function tuiGetMarkRange(pos, type, attributes = {}) {
  if (!pos || !type) {
    return null;
  }
  const {
    node,
    offset
  } = pos.parent.childAfter(pos.parentOffset);
  if (!node) {
    return null;
  }
  const mark = findMarkInSet(node.marks, type, attributes);
  if (!mark) {
    return null;
  }
  let startIndex = pos.index();
  let startPos = pos.start() + offset;
  let endIndex = startIndex + 1;
  let endPos = startPos + node.nodeSize;
  findMarkInSet(node.marks, type, attributes);
  while (startIndex > 0 && mark.isInSet(pos.parent.child(startIndex - 1).marks)) {
    startIndex -= 1;
    startPos -= pos.parent.child(startIndex).nodeSize;
  }
  while (endIndex < pos.parent.childCount && !!findMarkInSet(pos.parent.child(endIndex).marks, type, attributes)) {
    endPos += pos.parent.child(endIndex).nodeSize;
    endIndex += 1;
  }
  return {
    from: startPos,
    to: endPos
  };
}
function tuiGetNestedNodes(node) {
  const nodes = [];
  // @note: the content field is not array type
  node.content.forEach(child => {
    if (child instanceof Node) {
      nodes.push([child.type.name, child.attrs]);
    }
  });
  return nodes;
}
function tuiGetSelectedContent(state, current) {
  const currentNodeContent = current ?? state.selection.$head.parent.textContent;
  const selected = state.tr.doc.cut(state.tr.selection.from, state.tr.selection.to);
  return selected.content.size ? getHTMLFromFragment(selected.content, state.schema) : currentNodeContent;
}
function tuiGetSelectionState(editor) {
  if (!editor?.state?.selection) {
    return {
      before: '',
      after: ''
    };
  }
  const {
    $from,
    $to
  } = editor.state.selection;
  let before = $from.nodeBefore?.textContent;
  before = (before?.slice(((before?.lastIndexOf(' ') || before?.lastIndexOf('\n')) ?? 0) + 1) ?? '').trim();
  const after = $to.nodeAfter?.textContent.trim() ?? '';
  return {
    before,
    after
  };
}
function tuiGetSlicedFragment({
  schema,
  tr
}) {
  const selected = tr.doc.cut(tr.selection.from, tr.selection.to);
  return getHTMLFromFragment(selected.content, schema).replaceAll(/<\/?[^>]+(>|$)/g, '');
}

/**
 * Checks if selection is inside a specific selector
 * @param selection
 * @param selector
 * @return true if selection is completely inside a particular selector
 */
function tuiIsSelectionIn({
  anchorNode,
  focusNode
}, selector) {
  // Even though focusNode/anchor-node are defined as Node, they can be null on initial nested document query
  return !!anchorNode && !!focusNode && tuiIsNodeIn(anchorNode, selector) && tuiIsNodeIn(focusNode, selector);
}
function migration(element) {
  Array.from(element.children).forEach(child => {
    if (child.children.length) {
      migration(child);
    }
    if (child.tagName === 'FONT') {
      if (child.hasAttribute('size')) {
        switch (child.getAttribute('size')) {
          case '2':
            {
              migrateParagraph('13px', child);
              break;
            }
          case '3':
            {
              migrateParagraph('15px', child);
              break;
            }
          case '4':
            {
              migrateParagraph('17px', child);
              break;
            }
          case '5':
            {
              migrateHeading('h2', child);
              break;
            }
          case '6':
            {
              migrateHeading('h1', child);
              break;
            }
          default:
            break;
        }
      } else if (child.hasAttribute('color')) {
        migrateFontHighlight(child);
      }
    }
  });
}
function migrateHeading(selector, element) {
  if (globalThis.document) {
    const heading = document.createElement(selector);
    heading.innerHTML = element.innerHTML;
    if (element.parentElement?.tagName === 'P') {
      const newRef = element.parentElement?.parentElement?.insertBefore(heading, element.parentElement);
      element.parentElement?.removeChild(element);
      if (newRef?.nextSibling && !newRef?.nextSibling?.textContent?.trim()) {
        newRef.nextSibling?.parentElement?.removeChild(newRef.nextSibling);
      }
    } else {
      element.parentElement?.replaceChild(heading, element);
    }
  }
}
function migrateParagraph(fontSize, element) {
  if (globalThis.document) {
    const p = document.createElement('p');
    const span = document.createElement('span');
    span.setAttribute('style', `font-size: ${fontSize}`);
    span.innerHTML = element.innerHTML;
    p.append(span);
    element.parentElement?.replaceChild(element.parentElement.tagName === 'P' ? span : p, element);
  }
}
function migrateFontHighlight(element) {
  if (globalThis.document) {
    const span = document.createElement('span');
    span.setAttribute('style', `color: ${element.getAttribute('color')}`);
    span.innerHTML = element.innerHTML;
    element.parentElement?.replaceChild(span, element);
  }
}
/**
 * @description:
 * This converter help you to painlessly migrate old tags to the tui-editor[new]
 */
function tuiLegacyEditorConverter(content) {
  const tree = new DOMParser().parseFromString(content, 'text/html');
  migration(tree.body);
  return tree.body.innerHTML;
}
function tuiParseNodeAttributes(attrs) {
  return attrs.reduce((result, attribute) => {
    result[attribute] = {
      parseHTML: element => element?.getAttribute(`${attribute}`)
    };
    return result;
  }, {});
}
function tuiParseStyle(style) {
  return style.split(';').reduce((ruleMap, ruleString) => {
    const [left, right] = ruleString.split(':') ?? [];
    if (left && right) {
      ruleMap[left.trim()] = right.trim();
    }
    return ruleMap;
  }, {});
}
function tuiIsSafeLinkRange(range) {
  const textNodeLength = range.endContainer.nodeValue?.length ?? 0;
  return range.endOffset - range.startOffset > 0 || range.endOffset - range.startOffset === 0 && textNodeLength === 1 || range.startOffset !== 0 && textNodeLength > 1 && range.endOffset !== textNodeLength;
}
function tuiToGradient({
  stops,
  side
}) {
  return `linear-gradient(${side}, ${stops.map(({
    color,
    position
  }) => `rgba(${tuiParseColor(color).join(', ')}) ${position}`).join(', ')})`;
}

/**
 * Generated bundle index. Do not edit.
 */

export { tuiDeleteNode, tuiGetCurrentWordBounds, tuiGetMarkRange, tuiGetNestedNodes, tuiGetSelectedContent, tuiGetSelectionState, tuiGetSlicedFragment, tuiIsSafeLinkRange, tuiIsSelectionIn, tuiLegacyEditorConverter, tuiParseNodeAttributes, tuiParseStyle, tuiToGradient };
